import { isFixtureMode } from "./memberService";
import { supabase } from "./supabase";
import type {
  QaGateCheck,
  QaGateResult,
  QaPersona,
  QaRun,
  QaRunState,
  QaRunSummary,
  TaskAssignment,
  TaskOffer,
  WorkRoute,
} from "./workflowTypes";

const QA_KEY = "the-house-admin-qa-v1";

type QaRow = Record<string, unknown>;

interface QaFixtureState {
  runs: QaRun[];
}

export const qaRoutes: WorkRoute[] = ["clipper", "seeder", "closer", "scout", "promoter"];

export const defaultQaPersona: QaPersona = {
  route: "closer",
  level: 4,
  trust: 80,
  legalApproved: true,
  payoutEligible: true,
  activeTaskState: "none",
};

export function evaluateQaGate(
  offer: TaskOffer,
  persona: QaPersona,
  paidMissionsOpen: boolean,
  capacityFull = false,
): QaGateResult {
  const checks: QaGateCheck[] = [
    {
      key: "route",
      label: "Route match",
      passed: offer.route === persona.route,
      reason: offer.route === persona.route
        ? `Persona is on ${persona.route}.`
        : `Persona is ${persona.route}; this task is for ${offer.route}.`,
    },
    {
      key: "level",
      label: "Level",
      passed: offer.minimumLevel == null || persona.level >= offer.minimumLevel,
      reason: offer.minimumLevel == null || persona.level >= offer.minimumLevel
        ? `Persona level ${persona.level} is high enough.`
        : `Needs level ${offer.minimumLevel}; persona is level ${persona.level}.`,
    },
    {
      key: "trust",
      label: "Trust",
      passed: offer.minimumTrust == null || persona.trust >= offer.minimumTrust,
      reason: offer.minimumTrust == null || persona.trust >= offer.minimumTrust
        ? `Persona trust ${persona.trust} is high enough.`
        : `Needs trust ${offer.minimumTrust}; persona has ${persona.trust}.`,
    },
    {
      key: "legal",
      label: "Legal approved",
      passed: persona.legalApproved,
      reason: persona.legalApproved ? "Legal approval is on." : "Legal approval is missing.",
    },
    {
      key: "payout",
      label: "Payout eligible",
      passed: persona.payoutEligible,
      reason: persona.payoutEligible ? "Payout eligibility is on." : "Payout eligibility is missing.",
    },
    {
      key: "active_task",
      label: "No active task",
      passed: persona.activeTaskState === "none",
      reason: persona.activeTaskState === "none"
        ? "Persona has no active task."
        : `Persona already has ${persona.activeTaskState.replace("_", " ")} work.`,
    },
    {
      key: "capacity",
      label: "Capacity",
      passed: !capacityFull,
      reason: capacityFull ? "Workflow capacity is full." : "Capacity is available.",
    },
    {
      key: "paid_missions",
      label: "Paid launch flag",
      passed: paidMissionsOpen,
      reason: paidMissionsOpen
        ? "Real paid tasks are open."
        : "Real workers are blocked because paid tasks are still closed.",
    },
  ];
  const personaChecks = checks.filter((check) => check.key !== "paid_missions");
  return {
    offerId: offer.id,
    claimable: checks.every((check) => check.passed),
    qaRunnable: personaChecks.every((check) => check.passed),
    wouldPayUsd: offer.rewardUsd,
    checks,
    reasons: checks.filter((check) => !check.passed).map((check) => check.reason),
  };
}

export async function listQaRuns(): Promise<QaRunSummary[]> {
  if (isFixtureMode) {
    const offers = await fixtureOffers();
    return readFixtureQa().runs.map((run) => summarizeRun(run, offers));
  }
  const { data, error } = await supabase!.from("qa_runs").select("*").order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map((row) => summarizeRun(mapQaRun(row), []));
}

export async function startQaRun(persona: QaPersona): Promise<QaRun> {
  if (isFixtureMode) return fixtureStartQaRun(persona);
  const { data, error } = await supabase!.rpc("admin_start_qa_run", {
    route: persona.route,
    level: persona.level,
    trust: persona.trust,
    legalApproved: persona.legalApproved,
    payoutEligible: persona.payoutEligible,
  });
  if (error) throw error;
  return getQaRun(String(data));
}

export async function startQaFromOffer(offerId: string): Promise<QaRun> {
  if (isFixtureMode) return fixtureStartQaFromOffer(offerId);
  const { data, error } = await supabase!.rpc("admin_start_qa_from_offer", { p_offer_id: offerId });
  if (error) throw error;
  return getQaRun(String(data));
}

export async function claimQaTask(qaRunId: string, offerId: string, gate: QaGateResult): Promise<QaRun> {
  if (isFixtureMode) return fixtureClaimQaTask(qaRunId, offerId, gate);
  const { error } = await supabase!.rpc("admin_claim_qa_task", { qaRunId, offerId });
  if (error) throw error;
  return getQaRun(qaRunId);
}

export async function submitQaProof(qaRunId: string, evidence: Record<string, string>): Promise<QaRun> {
  if (isFixtureMode) return fixtureSubmitQaProof(qaRunId, evidence);
  const { error } = await supabase!.rpc("admin_submit_qa_proof", { qaRunId, evidence });
  if (error) throw error;
  return getQaRun(qaRunId);
}

export async function resolveQaReview(
  qaRunId: string,
  decision: "approved" | "rejected",
  notes: string,
): Promise<QaRun> {
  if (isFixtureMode) return fixtureResolveQaReview(qaRunId, decision, notes);
  const { error } = await supabase!.rpc("admin_resolve_qa_review", { qaRunId, decision, notes });
  if (error) throw error;
  return getQaRun(qaRunId);
}

export async function clearQaRun(qaRunId: string): Promise<QaRun> {
  if (isFixtureMode) return fixtureClearQaRun(qaRunId);
  const { error } = await supabase!.rpc("admin_clear_qa_run", { qaRunId });
  if (error) throw error;
  return getQaRun(qaRunId);
}

export async function getQaRun(id: string): Promise<QaRun> {
  if (isFixtureMode) {
    const run = readFixtureQa().runs.find((item) => item.id === id);
    if (!run) throw new Error("QA run not found.");
    return run;
  }
  const { data, error } = await supabase!.from("qa_runs").select("*").eq("id", id).single();
  if (error) throw error;
  return mapQaRun(data);
}

function mapQaRun(row: QaRow): QaRun {
  const persona = normalizePersona(row.persona);
  return {
    id: String(row.id),
    adminId: String(row.admin_id),
    state: normalizeQaState(row.state),
    persona,
    offerId: row.offer_id ? String(row.offer_id) : undefined,
    workflowId: row.workflow_id ? String(row.workflow_id) : undefined,
    assignmentId: row.assignment_id ? String(row.assignment_id) : undefined,
    submissionId: row.submission_id ? String(row.submission_id) : undefined,
    reviewId: row.review_id ? String(row.review_id) : undefined,
    evidence: normalizeEvidence(row.proof_snapshot),
    gateSnapshot: row.gate_snapshot && typeof row.gate_snapshot === "object"
      ? row.gate_snapshot as QaGateResult
      : undefined,
    reviewNotes: row.review_notes ? String(row.review_notes) : undefined,
    decision: row.decision === "approved" || row.decision === "rejected" ? row.decision : undefined,
    wouldPayUsd: Number(row.would_pay_usd ?? 0),
    qaOnly: true,
    createdAt: String(row.created_at),
    updatedAt: String(row.updated_at),
    resolvedAt: row.resolved_at ? String(row.resolved_at) : undefined,
  };
}

function normalizePersona(value: unknown): QaPersona {
  const raw = value && typeof value === "object" ? value as Record<string, unknown> : {};
  return {
    route: qaRoutes.includes(raw.route as WorkRoute) ? raw.route as WorkRoute : defaultQaPersona.route,
    level: clamp(Number(raw.level ?? defaultQaPersona.level), 0, 10),
    trust: clamp(Number(raw.trust ?? defaultQaPersona.trust), 0, 100),
    legalApproved: raw.legalApproved !== false,
    payoutEligible: raw.payoutEligible !== false,
    activeTaskState: raw.activeTaskState === "active" || raw.activeTaskState === "submitted" || raw.activeTaskState === "changes_requested"
      ? raw.activeTaskState
      : "none",
  };
}

function normalizeEvidence(value: unknown): Record<string, string> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([key, item]) => [key, String(item ?? "")]));
}

function normalizeQaState(value: unknown): QaRunState {
  return value === "claimed" || value === "submitted" || value === "approved" || value === "rejected" || value === "cleared"
    ? value
    : "created";
}

function summarizeRun(run: QaRun, offers: TaskOffer[]): QaRunSummary {
  return {
    id: run.id,
    state: run.state,
    route: run.persona.route,
    level: run.persona.level,
    trust: run.persona.trust,
    offerTitle: offers.find((offer) => offer.id === run.offerId)?.title,
    wouldPayUsd: run.wouldPayUsd,
    createdAt: run.createdAt,
    qaOnly: true,
  };
}

function readFixtureQa(): QaFixtureState {
  try {
    return JSON.parse(localStorage.getItem(QA_KEY) ?? "{\"runs\":[]}") as QaFixtureState;
  } catch {
    return { runs: [] };
  }
}

function writeFixtureQa(state: QaFixtureState) {
  localStorage.setItem(QA_KEY, JSON.stringify(state));
}

async function fixtureOffers() {
  if (!import.meta.env.DEV) return [];
  return (await import("./workflowFixtures")).listFixtureOffers();
}

export async function getFixtureQaActiveTask(qaRunId: string): Promise<TaskAssignment | null> {
  const state = readFixtureQa();
  const run = state.runs.find((item) => item.id === qaRunId);
  if (!run?.offerId || !run.assignmentId) return null;
  const workflow = await import("./workflowFixtures");
  const active = workflow.getFixtureActiveTask();
  if (active?.assignmentId === run.assignmentId) return { ...active, state: run.state === "submitted" ? "submitted" : "active" };
  const offer = (await fixtureOffers()).find((item) => item.id === run.offerId);
  if (!offer) return null;
  return {
    ...offer,
    assignmentId: run.assignmentId,
    state: run.state === "submitted" ? "submitted" : "active",
    acceptedAt: run.updatedAt,
    progress: Object.fromEntries(offer.steps.map((step) => [step.key, step.inputType === "check" ? true : `QA proof for ${step.title}`])),
    evidence: Object.keys(run.evidence).length ? run.evidence : defaultEvidenceForOffer(offer),
  };
}

async function fixtureStartQaFromOffer(offerId: string): Promise<QaRun> {
  const workflow = await import("./workflowFixtures");
  const offer = (await fixtureOffers()).find((item) => item.id === offerId);
  if (!offer) throw new Error("QA task is no longer available.");
  const persona: QaPersona = {
    route: offer.route,
    level: Math.max(offer.minimumLevel ?? 4, 4),
    trust: Math.max(offer.minimumTrust ?? 80, 80),
    legalApproved: true,
    payoutEligible: true,
    activeTaskState: "none",
  };
  const run = fixtureStartQaRun(persona);
  const claimed = await fixtureClaimQaTask(run.id, offer.id, evaluateQaGate(offer, persona, false));
  try {
    const active = workflow.acceptFixtureOffer(offerId);
    claimed.assignmentId = active.assignmentId;
    claimed.updatedAt = new Date().toISOString();
    const state = readFixtureQa();
    const stored = findFixtureRun(state, claimed.id);
    stored.assignmentId = active.assignmentId;
    stored.updatedAt = claimed.updatedAt;
    writeFixtureQa(state);
  } catch {
    // Fixture mode can already have a member task; the QA run still previews from the offer snapshot.
  }
  return claimed;
}

function fixtureStartQaRun(persona: QaPersona): QaRun {
  const state = readFixtureQa();
  const now = new Date().toISOString();
  const run: QaRun = {
    id: crypto.randomUUID(),
    adminId: "fixture-admin",
    state: "created",
    persona,
    evidence: {},
    wouldPayUsd: 0,
    qaOnly: true,
    createdAt: now,
    updatedAt: now,
  };
  state.runs.unshift(run);
  writeFixtureQa(state);
  return run;
}

async function fixtureClaimQaTask(qaRunId: string, offerId: string, gate: QaGateResult): Promise<QaRun> {
  const state = readFixtureQa();
  const run = findFixtureRun(state, qaRunId);
  const offer = (await fixtureOffers()).find((item) => item.id === offerId);
  if (!offer) throw new Error("QA task is no longer available.");
  if (!gate.qaRunnable) throw new Error("Persona gates must pass before a QA run can claim this task.");
  run.state = "claimed";
  run.offerId = offerId;
  run.workflowId = offer.taskId;
  run.assignmentId = `qa-assignment-${crypto.randomUUID()}`;
  run.gateSnapshot = gate;
  run.wouldPayUsd = offer.rewardUsd;
  run.updatedAt = new Date().toISOString();
  writeFixtureQa(state);
  return run;
}

function fixtureSubmitQaProof(qaRunId: string, evidence: Record<string, string>): QaRun {
  const state = readFixtureQa();
  const run = findFixtureRun(state, qaRunId);
  if (run.state !== "claimed") throw new Error("Claim a QA task before submitting proof.");
  run.state = "submitted";
  run.submissionId = `qa-submission-${crypto.randomUUID()}`;
  run.reviewId = `qa-review-${crypto.randomUUID()}`;
  run.evidence = evidence;
  run.updatedAt = new Date().toISOString();
  writeFixtureQa(state);
  return run;
}

function fixtureResolveQaReview(qaRunId: string, decision: "approved" | "rejected", notes: string): QaRun {
  const state = readFixtureQa();
  const run = findFixtureRun(state, qaRunId);
  if (run.state !== "submitted") throw new Error("Submit QA proof before resolving review.");
  run.state = decision;
  run.decision = decision;
  run.reviewNotes = notes;
  run.resolvedAt = new Date().toISOString();
  run.updatedAt = run.resolvedAt;
  writeFixtureQa(state);
  return run;
}

function fixtureClearQaRun(qaRunId: string): QaRun {
  const state = readFixtureQa();
  const run = findFixtureRun(state, qaRunId);
  run.state = "cleared";
  run.updatedAt = new Date().toISOString();
  writeFixtureQa(state);
  return run;
}

function defaultEvidenceForOffer(offer: TaskOffer) {
  return Object.fromEntries(offer.evidenceRequirements.map((item) => [
    item.key,
    item.kind === "url" ? "https://example.com/internal-qa-proof" : `QA proof for ${item.label}`,
  ]));
}

function findFixtureRun(state: QaFixtureState, id: string) {
  const run = state.runs.find((item) => item.id === id);
  if (!run) throw new Error("QA run not found.");
  return run;
}

function clamp(value: number, min: number, max: number) {
  return Number.isFinite(value) ? Math.max(min, Math.min(max, value)) : min;
}
